import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Button, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select } from '@mui/material';
import { CountryEntity, SourceOfWealth as SourceOfWealthType } from '@shared/api';
import { Savings } from './savings';
import { Employment } from './employment';
import { FamilyWealth } from './familyWealth';
import { Inheritance } from './inheritance';
import { Business } from './business';
import { SalesOfAssets } from './salesOfAssets';
import { Guarantor } from './guarantor';
import { Constants } from '@shared/constants';

const SOURCES_OF_WEALTH_OPTIONS = Object.keys(Constants.SOURCES_OF_WEALTH).map((key) => ({
  value: key,
  label: Constants.SOURCES_OF_WEALTH[key as SourceOfWealthType],
}));

interface SourceOfWealthProps {
  name: string;
  index: number;
  caption: string;
  countries: CountryEntity[];
  economicActivities: string[];
  onRemove?: () => void;
}

export const SourceOfWealth = (props: SourceOfWealthProps) => {
  const [sourceKey, setSourceKey] = useState<number>(0);

  const { control, watch, setValue } = useFormContext();
  const source = watch(`${props.name}[${props.index}].source`);
  const sourceOfSavings = watch(`${props.name}[${props.index}].sourceOfSavings.source`);

  useEffect(() => setSourceKey((prev) => prev + 1), [source]);

  return (
    <>
      <Grid item xs={12}>
        <Controller
          name={`${props.name}[${props.index}].source`}
          control={control}
          rules={{ required: `${props.caption} is required` }}
          render={({ field, fieldState: { error } }) => (
            <>
              <FormControl margin='normal' fullWidth error={!!error}>
                <InputLabel>{props.caption} *</InputLabel>
                <Select
                  {...field}
                  key={sourceKey}
                  value={field.value ?? ''}
                  variant='standard'
                  fullWidth
                  onChange={(e) => {
                    setValue(`${props.name}[${props.index}]`, {});
                    if (e.target.value === 'Savings') setValue(`${props.name}[${props.index}].sourceOfSavings.source`, '');
                    field.onChange(e);
                  }}
                >
                  {SOURCES_OF_WEALTH_OPTIONS.map((source, idx) => (
                    <MenuItem key={idx} value={source.value}>
                      {source.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{error?.message}</FormHelperText>
              </FormControl>
            </>
          )}
        />
      </Grid>
      {source === 'Savings' && <Savings name={`${props.name}[${props.index}]`} />}
      {(source === 'Employment' || sourceOfSavings === 'Employment') && (
        <Employment
          name={`${props.name}[${props.index}]${sourceOfSavings ? '.sourceOfSavings' : ''}`}
          countries={props.countries}
          economicActivities={props.economicActivities}
        />
      )}
      {(source === 'FamilyWealth' || sourceOfSavings === 'FamilyWealth') && (
        <FamilyWealth name={`${props.name}[${props.index}]${sourceOfSavings ? '.sourceOfSavings' : ''}`} countries={props.countries} />
      )}
      {(source === 'Inheritance' || sourceOfSavings === 'Inheritance') && (
        <Inheritance name={`${props.name}[${props.index}]${sourceOfSavings ? '.sourceOfSavings' : ''}`} countries={props.countries} />
      )}
      {(source === 'Business' || sourceOfSavings === 'Business') && (
        <Business
          name={`${props.name}[${props.index}]${sourceOfSavings ? '.sourceOfSavings' : ''}`}
          countries={props.countries}
          economicActivities={props.economicActivities}
        />
      )}
      {(source === 'SalesOfAssets' || sourceOfSavings === 'SalesOfAssets') && (
        <SalesOfAssets name={`${props.name}[${props.index}]${sourceOfSavings ? '.sourceOfSavings' : ''}`} countries={props.countries} />
      )}
      {source === 'Guarantor' && <Guarantor name={`${props.name}[${props.index}]`} />}
      {props.onRemove && (
        <Grid item xs={12}>
          <Button onClick={props.onRemove}>Remove</Button>
        </Grid>
      )}
    </>
  );
};
